import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, Calendar, TrendingDown, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtMoney = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AgingPage() {
    const navigate = useNavigate();
    const [type, setType] = useState('receivable');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(() => {
        setLoading(true);
        axios.get(`${API}/api/accounting/aging?type=${type}`, { headers: headers() })
            .then(r => setData(r.data))
            .catch(() => toast.error('Yuklenemedi'))
            .finally(() => setLoading(false));
    }, [type]);
    useEffect(() => { fetchData(); }, [fetchData]);

    const isReceivable = type === 'receivable';

    return (
        <DashboardLayout>
            <div className="space-y-4" data-testid="acc-aging-page">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/accounting')}
                        className="h-9 px-3 bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] text-[#374151] text-sm font-medium rounded-xl flex items-center gap-1.5"
                        data-testid="acc-aging-back-btn">
                        <ArrowLeft size={14} /> Muhasebe
                    </button>
                    <h1 className="text-xl font-bold text-[#111827]">Yaslandirma Raporu</h1>
                </div>

                {/* Tip toggle */}
                <div className="flex gap-1 bg-[#F3F4F6] rounded-xl p-1 w-fit">
                    <button onClick={() => setType('receivable')}
                        className={`px-4 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${type === 'receivable' ? 'bg-white text-emerald-600 shadow-sm' : 'text-[#6B7280]'}`}
                        data-testid="acc-aging-type-receivable">
                        <TrendingUp size={11} />Alacaklar (Musteri)
                    </button>
                    <button onClick={() => setType('payable')}
                        className={`px-4 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${type === 'payable' ? 'bg-white text-red-500 shadow-sm' : 'text-[#6B7280]'}`}
                        data-testid="acc-aging-type-payable">
                        <TrendingDown size={11} />Borclar (Tedarikci)
                    </button>
                </div>

                {loading ? <div className="p-12 text-center text-sm text-[#9CA3AF]">Yukleniyor...</div> :
                 !data || data.totals.company_count === 0 ? (
                    <div className="bg-white rounded-xl border border-[#E5E7EB] p-12 text-center">
                        <Calendar size={32} className="mx-auto mb-2 text-emerald-500" />
                        <p className="text-sm font-semibold text-[#111]">Hic acik {isReceivable ? 'alacak' : 'borc'} yok</p>
                    </div>
                ) : (
                    <>
                        {/* Toplam KPI'lar */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            <Kpi label="Henuz Vadesi Gelmemis" value={data.totals.not_yet_due} color="text-[#6B7280]" testId="acc-aging-kpi-not_yet_due" />
                            <Kpi label="0-30 gun" value={data.totals.b_0_30} color="text-amber-500" testId="acc-aging-kpi-0_30" />
                            <Kpi label="31-60 gun" value={data.totals.b_31_60} color="text-orange-500" testId="acc-aging-kpi-31_60" />
                            <Kpi label="61-90 gun" value={data.totals.b_61_90} color="text-red-500" testId="acc-aging-kpi-61_90" />
                            <Kpi label="90+ gun" value={data.totals.b_90_plus} color="text-red-700 font-bold" testId="acc-aging-kpi-90plus" />
                        </div>
                        <div className="bg-[#111827] text-white rounded-xl px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="acc-aging-grand-total">
                            <span className="text-sm">Toplam {isReceivable ? 'Alacak' : 'Borc'} <strong className="ml-2">{data.totals.company_count}</strong> firma</span>
                            <span className="text-xl font-bold">{fmtMoney(data.totals.total)} EUR</span>
                        </div>

                        {/* Tablo */}
                        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-[#FAFAFA]">
                                        <tr className="text-[10px] text-[#9CA3AF] uppercase">
                                            <th className="text-left px-3 py-2 font-semibold">Firma</th>
                                            <th className="text-right px-3 py-2 font-semibold">Vadesi Gelmemis</th>
                                            <th className="text-right px-3 py-2 font-semibold text-amber-600">0-30</th>
                                            <th className="text-right px-3 py-2 font-semibold text-orange-600">31-60</th>
                                            <th className="text-right px-3 py-2 font-semibold text-red-500">61-90</th>
                                            <th className="text-right px-3 py-2 font-semibold text-red-700">90+</th>
                                            <th className="text-right px-3 py-2 font-semibold">Toplam</th>
                                            <th className="text-center px-3 py-2 font-semibold">Fatura</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#F3F4F6]">
                                        {data.rows.map((r, i) => {
                                            const overdue = r.b_31_60 + r.b_61_90 + r.b_90_plus;
                                            const warn = overdue > 0;
                                            return (
                                                <tr key={i} className={`hover:bg-[#F9FAFB] ${warn ? 'bg-red-50/30' : ''}`} data-testid={`acc-aging-row-${i}`}>
                                                    <td className="px-3 py-2 max-w-[260px]">
                                                        {r.contact_id ? (
                                                            <button onClick={() => navigate(`/accounting/companies/${r.contact_id}`)}
                                                                className="text-sm font-medium text-[#111] hover:text-[#007AFF] hover:underline truncate text-left block w-full"
                                                                data-testid={`acc-aging-name-${i}`}>
                                                                {r.name}
                                                            </button>
                                                        ) : (
                                                            <span className="text-sm font-medium text-[#111] truncate">{r.name}</span>
                                                        )}
                                                        {r.oldest_due && <div className="text-[10px] text-[#9CA3AF]">En eski vade: {r.oldest_due}</div>}
                                                    </td>
                                                    <Cell v={r.not_yet_due} mute />
                                                    <Cell v={r.b_0_30} color="text-amber-600" />
                                                    <Cell v={r.b_31_60} color="text-orange-600" />
                                                    <Cell v={r.b_61_90} color="text-red-500" />
                                                    <Cell v={r.b_90_plus} color="text-red-700 font-bold" />
                                                    <td className="px-3 py-2 text-right text-sm font-bold text-[#111]">{fmtMoney(r.total)}</td>
                                                    <td className="px-3 py-2 text-center text-xs text-[#6B7280]">{r.invoice_count}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </DashboardLayout>
    );
}

function Kpi({ label, value, color, testId }) {
    return (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-3" data-testid={testId}>
            <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-sm font-bold ${color || 'text-[#111]'}`}>{fmtMoney(value)} EUR</p>
        </div>
    );
}

function Cell({ v, color, mute }) {
    if (!v || Math.abs(v) < 0.01) return <td className="px-3 py-2 text-right text-xs text-[#D1D5DB]">—</td>;
    return <td className={`px-3 py-2 text-right text-xs ${mute ? 'text-[#6B7280]' : color || 'text-[#111]'}`}>{fmtMoney(v)}</td>;
}
